import { State } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { UserOnboardingState } from '../onboarding/UserOnboardingState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_FOREGROUNDED } from '../AppContainer';
import { HistoryViewModel } from '../support/HistoryViewModel';

const SEARCH_PARAM_KEY = 'openChat';
export const HELPER_BEE_OPENED_STORAGE_KEY = 'helper-bee-opened';

export type IBeeChatTrackingFrom = 'Welcome' | 'Caregiver Tools' | 'Onboarding To Do' | 'Onboarding Revisited' | 'Deep Link' | 'Care Recipient Added' | 'Header Button' | 'Bea Comment';

export class BeeChatViewModel extends State<Record<string, never>> {
    private _isOpen = false;
    private _isMinimized = false;
    private _restoringFromMinimize = false;

    // Location-specific ping tracking (home page vs header pages)
    private _homePingsEligible = true;
    private _homePingsActive = false;
    private _homeTimer: NodeJS.Timeout | null = null;

    private _headerPingsEligible = true;
    private _headerPingsActive = false;
    private _headerTimer: NodeJS.Timeout | null = null;

    private _isFirstForegroundEvent = true;

    static inject = () => [
        MixPanelEventEmitter,
        AsyncStorageProvider,
        UserOnboardingState,
        BufferedEventBus,
        HistoryViewModel
    ];

    constructor(
        private mixpanel: MixPanelEventEmitter,
        private storageProvider: AsyncStorageProvider,
        private userOnboardingState: UserOnboardingState,
        private eventBus: BufferedEventBus,
        private history: HistoryViewModel
    ) {
        super({ });

        this.initSonarPings();

        this.history.listen(this.openIfSearchParam);
        this.openIfSearchParam();
    }

    private openIfSearchParam = () => {
        const params = this.history.getCurrentSearchParamMap();
        if (params[SEARCH_PARAM_KEY] !== undefined) {
            this.open({ trackingFrom: 'Deep Link' });
        }
    };

    private initSonarPings = () => {
        // Listen for app foreground to reset ping eligibility
        this.eventBus.on(APP_FOREGROUNDED, () => {
            // Skip the first buffered event (app is already initialized)
            if (this._isFirstForegroundEvent) {
                this._isFirstForegroundEvent = false;
                return;
            }
            this.resetPingEligibility();
        });
    };

    private resetPingEligibility = () => {
        // Clear any existing timers
        if (this._homeTimer) {
            clearTimeout(this._homeTimer);
        }
        if (this._headerTimer) {
            clearTimeout(this._headerTimer);
        }

        // Reset both locations to eligible
        this._homePingsEligible = true;
        this._homePingsActive = false;
        this._headerPingsEligible = true;
        this._headerPingsActive = false;
        this.setState({});
    };

    private startPingsFor = (location: 'home' | 'header') => {
        if (location === 'home') {
            if (!this._homePingsEligible || this._homePingsActive) {
                return;
            }
            this._homePingsActive = true;
            this.setState({});
            this._homeTimer = setTimeout(() => {
                this._homePingsActive = false;
                this._homePingsEligible = false;
                this.setState({});
            }, 7500);
        } else {
            if (!this._headerPingsEligible || this._headerPingsActive) {
                return;
            }
            this._headerPingsActive = true;
            this.setState({});
            this._headerTimer = setTimeout(() => {
                this._headerPingsActive = false;
                this._headerPingsEligible = false;
                this.setState({});
            }, 7500);
        }
    };

    // Call this in useEffect to trigger pings for a location
    triggerPings = (location: 'home' | 'header') => {
        this.startPingsFor(location);
    };

    // Call this during render to check if pings should show
    showHomePings = () => this._homePingsActive;
    showHeaderPings = () => this._headerPingsActive;

    isOpen = () => this._isOpen;
    isMinimized = () => this._isMinimized;
    isRestoringFromMinimize = () => this._restoringFromMinimize;
    clearRestoringFlag = () => { this._restoringFromMinimize = false; };

    private setStorageOpened = async () => {
        await this.storageProvider.getBooleanProvider(HELPER_BEE_OPENED_STORAGE_KEY).set(true);
    };

    open = (params: { trackingFrom: IBeeChatTrackingFrom }) => {
        // If minimized, restore instead of starting fresh
        if (this._isMinimized) {
            this.restore();
            return;
        }

        const { trackingFrom } = params;

        // Mark as opened for badge removal and onboarding completion
        this.setStorageOpened();
        this.userOnboardingState.completeStep('opened-bea');

        this._isOpen = true;
        this.setState({ });

        this.mixpanel.track('Native AI Chatbot Opened', () => ({ from: trackingFrom }));
    };

    minimize = () => {
        this._isOpen = false;
        this._isMinimized = true;
        this.setState({ });
        this.mixpanel.track('Native AI Chatbot Minimized', () => ({}));
    };

    restore = () => {
        this._isOpen = true;
        this._isMinimized = false;
        this._restoringFromMinimize = true;
        this.setState({ });
        this.mixpanel.track('Native AI Chatbot Restored', () => ({}));
    };

    close = () => {
        this._isOpen = false;
        this._isMinimized = false;
        this.history.removeSearchParam(SEARCH_PARAM_KEY);
        this.setState({ });
    };
}
